import {
  Definition,
  DefinitionList,
  DefinitionListItem,
  DefinitionTerm,
  DefinitionValue,
  Section,
} from '@apexinternal/ui-components'
import {
  Badge,
  Box,
  BoxProps,
  Flex,
  Text,
  VStack,
  Center,
} from '@chakra-ui/react'
import React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { EMPTY_ADDRESS, PermissionsEnum } from '../../constants'
import { usePermissions } from '../../hooks'
import { RootState } from '../../store'
import { AccountStatusDisplay, PlatformDisplay } from '../../types'
import {
  formatAddressStartCase,
  formatEnumValue,
  formatISODateToLongDate,
  formatPhoneNumbers,
  sentenceCase,
} from '../../utils'
import { FaExclamationCircle } from 'react-icons/fa'
import { upperCase } from 'lodash'
import { UpdateAddressModal, UpdatePhoneModal } from './components'
import AccountInformationHeader from './AccountInformationHeader'
import AccountNotes from './AccountNotes'
import { CloseAccountModal } from './components/CloseAccountModal'
import EmailUpdateButton from './components/EmailUpdateModal/EmailUpdateButton'
import { RestrictAccountModal } from './components/RestrictAccountModal'
import { isCustomerUpdateAllowedForAccount } from './isCustomerUpdateAllowedForAccount'

interface AccountInformationComponentProps extends BoxProps, PropsFromRedux {
  isSummary: boolean
}

const AccountInformationComponent = ({
  isSummary,
  accountDetails,
  accountNotes,
  correspondentName,
  tradingRestrictions,
  dispatch,
  ...rest
}: AccountInformationComponentProps) => {
  const { hasPermission } = usePermissions()
  const {
    accountNumber = '',
    repCode = '',
    restrictionCode = '',
    isActive = null,
    accountNames = [],
    accountTitle = '',
    jointOwnerName = '',
    accountAddress = EMPTY_ADDRESS,
    phoneNumbers = [],
    emailAddress = '',
    taxId = '',
    withholdingStatus,
    accountRegistration,
    isPatternDayTrader,
    marginType,
    openedDate,
    lastStatementDate,
    institutionalId,
  } = accountDetails || {}

  const formattedNames = accountNames
    ? accountNames.map(name => sentenceCase(name))
    : ['']
  const names = formattedNames
    .concat(
      sentenceCase(jointOwnerName) ? `& ${sentenceCase(jointOwnerName)}` : ''
    )
    .join(' ')
  const formattedAddress = accountAddress
    ? formatAddressStartCase(accountAddress)
    : ''
  const formattedTitle = sentenceCase(accountTitle)
  const nameAndAddress = [names].concat(formattedAddress)
  const titleNameAddress = [formattedTitle, ...nameAndAddress]
  const formattedPhoneNumbers = formatPhoneNumbers(phoneNumbers)
  const canSeeOrUpdateThirdPartyTradingStatus =
    hasPermission(PermissionsEnum.TradingKillSwitchRestrictionsGet) &&
    hasPermission(PermissionsEnum.TradingKillSwitchRestrictionsUpdate)
  const customerUpdateAllowedForAccount = isCustomerUpdateAllowedForAccount(
    accountDetails
  )

  return (
    <Section {...rest} data-testid="account-information">
      <AccountInformationHeader
        accountNumber={accountNumber}
        isSummary={isSummary}
      />
      <DefinitionList>
        {correspondentName && (
          <Definition
            term="Correspondent"
            definition={sentenceCase(correspondentName)}
          />
        )}
        {repCode && <Definition term="Rep. Code" definition={repCode} />}
        <DefinitionListItem>
          <DefinitionTerm>Account Status</DefinitionTerm>
          <DefinitionValue>
            {isActive !== null ? (
              <Flex align="self-start" justifyContent="space-between">
                <Badge
                  variant="solid"
                  padding="0.25rem 0.75rem"
                  colorScheme={isActive! ? 'green' : 'red'}
                >
                  {isActive! ? 'Open' : 'Closed'}
                </Badge>
                {isActive! && accountDetails && (
                  <CloseAccountModal accountDetails={accountDetails} />
                )}
              </Flex>
            ) : (
              ''
            )}
          </DefinitionValue>
        </DefinitionListItem>
        {restrictionCode && (
          <Definition
            term="Restriction"
            definition={formatEnumValue(restrictionCode)}
          />
        )}
        {canSeeOrUpdateThirdPartyTradingStatus && tradingRestrictions && (
          <DefinitionListItem>
            <DefinitionTerm>Third Party Trading Status</DefinitionTerm>
            <DefinitionValue>
              <Flex align="self-start" justifyContent="space-between">
                <Text>{`[${PlatformDisplay[tradingRestrictions.platform]}] ${
                  AccountStatusDisplay[tradingRestrictions.accountStatus]
                }`}</Text>
                <RestrictAccountModal accountNumber={accountNumber} />
              </Flex>
            </DefinitionValue>
          </DefinitionListItem>
        )}
        {(formattedAddress || formattedTitle || names) && (
          <DefinitionListItem>
            <DefinitionTerm>Name & Address</DefinitionTerm>
            <DefinitionValue>
              <Flex align="self-start" justifyContent="space-between">
                <Box>
                  {titleNameAddress.map((line, index) => (
                    <Text key={index}>{line}</Text>
                  ))}
                </Box>
                {customerUpdateAllowedForAccount && (
                  <UpdateAddressModal
                    accountNumber={accountNumber}
                    //@ts-ignore Property 'mailingAddress' is missing in type 'Address' but required in type 'Addresses'
                    accountAddress={accountAddress}
                  />
                )}
              </Flex>
            </DefinitionValue>
          </DefinitionListItem>
        )}
        <DefinitionListItem>
          <DefinitionTerm>Phone</DefinitionTerm>
          <DefinitionValue>
            <Flex
              justifyContent={phoneNumbers.length ? 'space-between' : 'end'}
            >
              <VStack align="start" spacing={0}>
                {formattedPhoneNumbers.map((phoneNumber, index) => (
                  <Text key={index}>{phoneNumber}</Text>
                ))}
              </VStack>
              {accountDetails && customerUpdateAllowedForAccount && (
                <UpdatePhoneModal
                  accountNumber={accountNumber}
                  phoneNumbers={phoneNumbers}
                />
              )}
            </Flex>
          </DefinitionValue>
        </DefinitionListItem>
        <DefinitionListItem>
          <DefinitionTerm>Email</DefinitionTerm>
          <DefinitionValue>
            <Flex align="self-start" justifyContent="space-between">
              <Text>{emailAddress}</Text>
              {customerUpdateAllowedForAccount && (
                <EmailUpdateButton
                  accountNumber={accountNumber}
                  emailAddress={emailAddress}
                />
              )}
            </Flex>
          </DefinitionValue>
        </DefinitionListItem>
        {taxId && <Definition term="Tax ID" definition={taxId} />}
        {withholdingStatus && (
          <Definition
            term="Withholding Status"
            definition={upperCase(withholdingStatus)}
          />
        )}
        {accountRegistration && (
          <Definition
            term="Account Registration"
            definition={upperCase(accountRegistration)}
          />
        )}
        {marginType && (
          <DefinitionListItem>
            <DefinitionTerm>Margin Type</DefinitionTerm>
            <DefinitionValue>
              <Flex direction="row">
                <Center>
                  <Text>{formatEnumValue(marginType)}</Text>
                  {isPatternDayTrader && (
                    <>
                      <Text fontWeight="bold" paddingX={1}>
                        -
                      </Text>
                      <Box paddingX={1}>
                        <FaExclamationCircle color="orange" size={16} />
                      </Box>
                      <Text fontWeight="bold">Pattern Day Trader</Text>
                    </>
                  )}
                </Center>
              </Flex>
            </DefinitionValue>
          </DefinitionListItem>
        )}
        {openedDate && (
          <Definition
            term="Opened Date"
            definition={formatISODateToLongDate(openedDate)}
          />
        )}
        {lastStatementDate && (
          <Definition
            term="Last Statement Date"
            definition={formatISODateToLongDate(lastStatementDate)}
          />
        )}
        {institutionalId && (
          <Definition term="Institutional ID" definition={institutionalId} />
        )}
        {accountNotes.length > 0 && (
          <AccountNotes accountNotes={accountNotes} />
        )}
      </DefinitionList>
    </Section>
  )
}

const mapStateToProps = ({
  accountDetailsReducer: { accountDetails },
  correspondentReducer: {
    correspondent: { name: correspondentName },
  },
  accountNotesReducer: { accountNotes },
  tradingRestrictionsReducer: { tradingRestrictions },
}: RootState) => ({
  accountDetails,
  accountNotes,
  correspondentName,
  tradingRestrictions,
})

const connector = connect(mapStateToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export const AccountInformation = connector(AccountInformationComponent)
